import { useState } from "react";
import { Facebook, Instagram, ArrowRight } from "lucide-react";
import { ThreadsIcon } from "@/components/common/ThreadsIcon";
import { TikTokIcon } from "@/components/common/TikTokIcon";
import { NewsletterModal } from "@/components/common/NewsletterModal";

/**
 * Site footer.
 *
 * Layout: four columns on desktop — Logo+Address, Hours, Connect, Newsletter.
 * Eyebrow labels (HOURS / CONNECT / NEWSLETTER) align on a single horizontal
 * line; the wordmark in the logo column stands in for an eyebrow there. The
 * Subscribe ghost button is the site's primary newsletter entry point and
 * opens the existing NewsletterModal (Klaviyo-backed) without changes.
 */
export function Footer() {
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  const eyebrowClass =
    "text-[#CBA365] uppercase font-medium mb-5";
  const eyebrowStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize: "0.75rem",
    letterSpacing: "0.32em",
  };
  const bodyClass = "text-sm text-left text-white/55 leading-relaxed";

  return (
    <footer data-testid="site-footer" className="bg-[#0A1128] text-white">
      <div
        className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-16 pb-8"
      >
        <div
          className="footer-grid grid items-start"
          style={{ gap: "4rem" }}
        >
          {/* Column 1 — Logo + Address (no eyebrow; wordmark holds the line) */}
          <div data-testid="footer-col-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_fine-art-frame/artifacts/ygkfc1mx_Untitled%20design%20(42).png"
              alt="Rain Boy Fine Art"
              className="h-16 brightness-0 invert"
            />
            <p
              className={bodyClass}
              style={{ marginTop: "0.25rem" }}
              data-testid="footer-address"
            >
              3306 Charles Page Blvd #218<br />Tulsa, OK 74127
            </p>
          </div>

          {/* Column 2 — Hours */}
          <div data-testid="footer-col-hours">
            <h4 className={eyebrowClass} style={eyebrowStyle}>Hours</h4>
            <div className={`${bodyClass} space-y-1`}>
              <p>Monday &ndash; Saturday: 11am &ndash; 6pm</p>
              <p>Sunday: By Appointment</p>
            </div>
          </div>

          {/* Column 3 — Connect */}
          <div data-testid="footer-col-connect">
            <h4 className={eyebrowClass} style={eyebrowStyle}>Connect</h4>
            <div className={`${bodyClass} space-y-1 mb-5`}>
              <p>(405) 885-9010</p>
              <p>info@rainboyfineart.com</p>
            </div>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61581586250780"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="text-white hover:text-[#CBA365] transition-colors"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.instagram.com/rainboyfineart"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="text-white hover:text-[#CBA365] transition-colors"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.threads.com/@rainboyfineart"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-threads"
                className="text-white hover:text-[#CBA365] transition-colors"
              >
                <ThreadsIcon size={18} />
              </a>
              <a
                href="https://www.tiktok.com/@rainboyfineart"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                className="text-white hover:text-[#CBA365] transition-colors"
              >
                <TikTokIcon size={18} />
              </a>
            </div>
          </div>

          {/* Column 4 — Newsletter */}
          <div data-testid="footer-col-newsletter">
            <h4 className={eyebrowClass} style={eyebrowStyle}>Newsletter</h4>
            <p className={bodyClass}>
              New work, exhibitions, and events &mdash; to your inbox.
            </p>
            <button
              type="button"
              onClick={() => setNewsletterOpen(true)}
              className="subscribe-ghost inline-flex items-center"
              style={{
                marginTop: "1.5rem",
                padding: "0.85rem 1.75rem",
                gap: "0.6rem",
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 500,
                fontSize: "0.8rem",
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
              data-testid="footer-newsletter-subscribe"
              aria-label="Subscribe to the Rain Boy Fine Art newsletter"
            >
              <span aria-hidden="true" className="subscribe-ghost__fill" />
              <span>Subscribe</span>
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-4 pb-1 flex flex-wrap justify-center gap-x-3 gap-y-1 text-center text-xs text-white/25">
          <span>
            &copy; {new Date().getFullYear()} Rain Boy Fine Art. All rights reserved.
          </span>
          <span aria-hidden="true" className="hidden sm:inline">&middot;</span>
          <span data-testid="footer-credit">
            Website by{" "}
            <a
              href="https://parker-local.com"
              target="_blank"
              rel="noopener"
              className="text-white/40 hover:text-[#CBA365] transition-colors"
            >
              Parker Local
            </a>
          </span>
        </div>
      </div>

      <NewsletterModal
        isOpen={newsletterOpen}
        onClose={() => setNewsletterOpen(false)}
      />
    </footer>
  );
}
